import { createSlice, createSelector } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import {
  SideSearchFilterState,
  CompanyCategory,
  CompanyRegion,
  CompanyEmployee,
  SearchFilterData,
} from '@/types/filter'

const initialState: SideSearchFilterState = {
  searchWord: '',
  companyCategories: [],
  companyRegion: [],
  companyEmployee: [],
  selectedCategories: [],
  selectedRegion: [],
  selectedEmployeeSize: [],
  openPanels: [],
  errorMessage: '',
  inputValidated: false,
  isIphone: false,
}

export const searchFilterSlice = createSlice({
  name: 'searchFilter',
  initialState,
  reducers: {
    setSearchWord: (state, action: PayloadAction<string>) => {
      state.searchWord = action.payload
    },
    setCompanyCategories: (state, action: PayloadAction<CompanyCategory[]>) => {
      state.companyCategories = action.payload
    },
    setCompanyRegion: (state, action: PayloadAction<CompanyRegion[]>) => {
      state.companyRegion = action.payload
    },
    setCompanyEmployee: (state, action: PayloadAction<CompanyEmployee[]>) => {
      state.companyEmployee = action.payload
    },
    setOpenPanels: (state, action: PayloadAction<number[]>) => {
      state.openPanels = action.payload
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    setInputValidated: (state, action: PayloadAction<boolean>) => {
      state.inputValidated = action.payload
    },
    setIsIphone: (state, action: PayloadAction<boolean>) => {
      state.isIphone = action.payload
    },
    toggleCategorySelection: (state, action: PayloadAction<CompanyCategory>) => {
      const category = action.payload
      const categoryIndex = state.companyCategories.findIndex((c) => c.id === category.id)

      if (categoryIndex >= 0) {
        state.companyCategories[categoryIndex].isSelected =
          !state.companyCategories[categoryIndex].isSelected

        if (state.companyCategories[categoryIndex].isSelected) {
          state.selectedCategories.push(category)
        } else {
          state.selectedCategories = state.selectedCategories.filter((c) => c.id !== category.id)
        }
      }

      // Handle sub-categories
      state.companyCategories.forEach((parentCat) => {
        if (parentCat.category) {
          parentCat.category.forEach((subCat, subIndex) => {
            if (subCat.id === category.id) {
              parentCat.category![subIndex].isSelected = !parentCat.category![subIndex].isSelected

              if (parentCat.category![subIndex].isSelected) {
                if (!state.selectedCategories.some((c) => c.id === category.id)) {
                  state.selectedCategories.push(category)
                }
              } else {
                state.selectedCategories = state.selectedCategories.filter(
                  (c) => c.id !== category.id,
                )
              }
            }
          })
        }
      })
    },
    toggleRegionSelection: (state, action: PayloadAction<CompanyRegion>) => {
      const region = action.payload
      const regionIndex = state.companyRegion.findIndex((r) => r.id === region.id)

      if (regionIndex >= 0) {
        state.companyRegion[regionIndex].isSelected = !state.companyRegion[regionIndex].isSelected

        if (state.companyRegion[regionIndex].isSelected) {
          state.selectedRegion.push(region)
        } else {
          state.selectedRegion = state.selectedRegion.filter((r) => r.id !== region.id)
        }
      }
    },
    toggleEmployeeSelection: (state, action: PayloadAction<CompanyEmployee>) => {
      const employee = action.payload
      const employeeIndex = state.companyEmployee.findIndex((e) => e.id === employee.id)

      if (employeeIndex >= 0) {
        state.companyEmployee[employeeIndex].isSelected =
          !state.companyEmployee[employeeIndex].isSelected

        if (state.companyEmployee[employeeIndex].isSelected) {
          state.selectedEmployeeSize.push(employee)
        } else {
          state.selectedEmployeeSize = state.selectedEmployeeSize.filter(
            (e) => e.id !== employee.id,
          )
        }
      }
    },
    selectAllCategories: (state, action: PayloadAction<CompanyCategory>) => {
      const parentCategory = action.payload
      const parentIndex = state.companyCategories.findIndex((c) => c.id === parentCategory.id)

      if (parentIndex >= 0 && state.companyCategories[parentIndex].category) {
        const allSelected = state.companyCategories[parentIndex].category!.every(
          (c) => c.isSelected,
        )
        const newState = !allSelected

        // Remove all sub-categories from selectedCategories if deselecting all
        if (!newState) {
          state.selectedCategories = state.selectedCategories.filter(
            (selected) =>
              !state.companyCategories[parentIndex].category!.some((sub) => sub.id === selected.id),
          )
        }

        // Update all sub-categories
        state.companyCategories[parentIndex].category!.forEach((subCat) => {
          subCat.isSelected = newState

          if (newState && !state.selectedCategories.some((c) => c.id === subCat.id)) {
            state.selectedCategories.push(subCat)
          }
        })
      }
    },
    clearAllSelections: (state) => {
      state.selectedCategories = []
      state.selectedRegion = []
      state.selectedEmployeeSize = []
      state.searchWord = ''

      state.companyCategories.forEach((category) => {
        category.isSelected = false
        if (category.category) {
          category.category.forEach((subCategory) => {
            subCategory.isSelected = false
          })
        }
      })

      state.companyRegion.forEach((region) => {
        region.isSelected = false
      })

      state.companyEmployee.forEach((employee) => {
        employee.isSelected = false
      })
    },
    updateSelectionsFromQuery: (state, action: PayloadAction<any>) => {
      const query = action.payload

      // クエリパラメータから検索ワードをクリア・更新
      state.searchWord = ''
      if (query.company_name) {
        state.searchWord = decodeURIComponent(query.company_name)
      } else if (query.query) {
        state.searchWord = decodeURIComponent(query.query)
      }

      // Clear previous selections
      state.selectedCategories = []
      state.selectedRegion = []
      state.selectedEmployeeSize = []

      // Ensure query parameters are arrays
      const cidArray = Array.isArray(query.cid) ? query.cid : []
      const ridArray = Array.isArray(query.rid) ? query.rid : []
      const eidArray = Array.isArray(query.eid) ? query.eid : []

      // Update categories
      state.companyCategories.forEach((category) => {
        category.isSelected = cidArray.includes(String(category.id))
        if (category.isSelected) {
          state.selectedCategories.push(category)
        }

        if (category.category) {
          category.category.forEach((subCategory) => {
            subCategory.isSelected = cidArray.includes(String(subCategory.id))
            if (subCategory.isSelected) {
              state.selectedCategories.push(subCategory)
            }
          })
        }
      })

      // Update regions
      state.companyRegion.forEach((region) => {
        region.isSelected = ridArray.includes(String(region.id))
        if (region.isSelected) {
          state.selectedRegion.push(region)
        }
      })

      // Update employee sizes
      state.companyEmployee.forEach((employee) => {
        employee.isSelected = eidArray.includes(String(employee.id))
        if (employee.isSelected) {
          state.selectedEmployeeSize.push(employee)
        }
      })
    },
  },
})

export const {
  setSearchWord,
  setCompanyCategories,
  setCompanyRegion,
  setCompanyEmployee,
  setOpenPanels,
  setErrorMessage,
  setInputValidated,
  setIsIphone,
  toggleCategorySelection,
  toggleRegionSelection,
  toggleEmployeeSelection,
  selectAllCategories,
  clearAllSelections,
  updateSelectionsFromQuery,
} = searchFilterSlice.actions

// Selectors
export const selectSearchWord = (state: RootState): string => state.searchFilter.searchWord

export const selectCompanyCategories = (state: RootState): CompanyCategory[] =>
  state.searchFilter.companyCategories

export const selectCompanyRegion = (state: RootState): CompanyRegion[] =>
  state.searchFilter.companyRegion

export const selectCompanyEmployee = (state: RootState): CompanyEmployee[] =>
  state.searchFilter.companyEmployee

export const selectSelectedCategories = (state: RootState): CompanyCategory[] =>
  state.searchFilter.selectedCategories

export const selectSelectedRegion = (state: RootState): CompanyRegion[] =>
  state.searchFilter.selectedRegion

export const selectSelectedEmployeeSize = (state: RootState): CompanyEmployee[] =>
  state.searchFilter.selectedEmployeeSize

export const selectOpenPanels = (state: RootState): number[] => state.searchFilter.openPanels

export const selectErrorMessage = (state: RootState): string => state.searchFilter.errorMessage

export const selectInputValidated = (state: RootState): boolean => state.searchFilter.inputValidated

export const selectIsIphone = (state: RootState): boolean => state.searchFilter.isIphone

export const selectSearchFilterData = createSelector(
  [selectSearchWord, selectSelectedCategories, selectSelectedRegion, selectSelectedEmployeeSize],
  (searchWord, selectedCategories, selectedRegion, selectedEmployeeSize): SearchFilterData => ({
    keyword: searchWord,
    cid: selectedCategories.map((c) => c.id),
    rid: selectedRegion.map((r) => r.id),
    eid: selectedEmployeeSize.map((e) => e.id),
  }),
)

export default searchFilterSlice.reducer
